// Is the game installed as a web app (home screen)? If not: ask for it, or explain how.
// iOS has no install prompt: the player has to use Share → Add to Home Screen.
// Android/Chromium fires `beforeinstallprompt`, which lets us show the system dialog.

import { backstage } from './backstage.js';
import { openSheet } from './sheet.js';
import { el } from './util.js';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type Platform = 'ios' | 'android' | 'desktop';
export interface InstallInfo {
  standalone: boolean;
  platform: Platform;
  browser: 'safari' | 'chrome' | 'firefox' | 'samsung' | 'edge' | 'other';
  /** Opened inside Instagram, Facebook, Messenger, ...: these cannot install web apps. */
  inApp: string | null;
  /** The system install dialog is available (Chromium). */
  canPrompt: boolean;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

// Must be registered as early as possible: the event fires shortly after load.
addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as BeforeInstallPromptEvent;
  listeners.forEach((fn) => fn());
});
addEventListener('appinstalled', () => {
  deferred = null;
  backstage.log('sensor', 'Webapp geïnstalleerd', 'Event <code>appinstalled</code>: het spel staat nu op het beginscherm.');
});

export function isStandalone(): boolean {
  return matchMedia('(display-mode: standalone)').matches
    || matchMedia('(display-mode: fullscreen)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function detect(): InstallInfo {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  const inAppMatch = ua.match(/(FBAN|FBAV|Instagram|Messenger|LinkedInApp|Snapchat|TikTok|Line\/|GSA\/|Twitter)/);
  const browser: InstallInfo['browser'] = /SamsungBrowser/.test(ua) ? 'samsung'
    : /EdgiOS|EdgA|Edg\//.test(ua) ? 'edge'
    : /FxiOS|Firefox/.test(ua) ? 'firefox'
    : /CriOS|Chrome/.test(ua) ? 'chrome'
    : /Safari/.test(ua) ? 'safari' : 'other';
  return {
    standalone: isStandalone(),
    platform: ios ? 'ios' : android ? 'android' : 'desktop',
    browser,
    inApp: inAppMatch ? inAppMatch[1].replace(/\/$/, '').replace(/^FBA[NV]$/, 'Facebook').replace('GSA', 'Google-app') : null,
    canPrompt: !!deferred,
  };
}

/** Chromium on Android can tell whether this web app is already installed (manifest: related_applications). */
export async function installedElsewhere(): Promise<boolean> {
  const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> };
  try { return !!nav.getInstalledRelatedApps && (await nav.getInstalledRelatedApps()).length > 0; } catch { return false; }
}

const ICON_SHARE = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V3M8 7l4-4 4 4"/><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1"/></svg>';
const ICON_ADD = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><rect x="4" y="4" width="16" height="16" rx="4"/><path d="M12 8v8M8 12h8"/></svg>';
const ICON_MENU = '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>';

function steps(items: Array<[string, string]>): HTMLElement {
  return el('ol.steps', {}, ...items.map(([icon, html]) => el('li', {}, el('span.steps__icon', { html: icon }), el('span', { html: html }))));
}

/** Platform-specific instructions. */
export function instructions(info: InstallInfo): HTMLElement {
  if (info.inApp) {
    const copy = el('button.btn.btn--ghost.btn--small', { type: 'button' }, 'Kopieer de link');
    copy.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(location.origin + '/'); copy.textContent = 'Gekopieerd'; } catch { copy.textContent = location.origin; }
    });
    return el('div', {},
      el('p.small', {}, `Je opende dit in de ${info.inApp}-app. Van daaruit kan het spel niet op je beginscherm.`),
      steps([
        [ICON_MENU, `Tik op het menu (<b>⋯</b>) van de ${info.inApp}-app`],
        [ICON_SHARE, `Kies <b>Open in ${info.platform === 'ios' ? 'Safari' : 'browser'}</b>, of kopieer de link en plak ze daar`],
      ]),
      copy);
  }
  if (info.platform === 'ios') {
    return el('div', {},
      steps([
        [ICON_SHARE, info.browser === 'safari'
          ? 'Tik onderaan (of naast de adresbalk) op <b>Deel</b>'
          : `Tik in ${info.browser === 'chrome' ? 'Chrome' : info.browser === 'firefox' ? 'Firefox' : 'je browser'} op <b>Deel</b> (naast de adresbalk). Lukt het niet, open dan deze pagina in Safari.`],
        [ICON_ADD, 'Scrol en kies <b>Zet op beginscherm</b>, dan <b>Voeg toe</b>'],
        ['<img src="/assets/apple-touch-icon.png" width="22" height="22" alt="" style="border-radius:5px">', 'Open <b>Kanaal</b> vanaf je beginscherm'],
      ]),
      el('p.small', {}, 'Op een iPhone werken meldingen alleen als het spel op je beginscherm staat (iOS 16.4 of nieuwer). Het spel begint daar opnieuw: wat je hier speelde, blijft in de browser.'));
  }
  if (info.platform === 'android') {
    return el('div', {},
      steps([
        [ICON_MENU, `Tik in ${info.browser === 'samsung' ? 'Samsung Internet onderaan op het menu (☰)' : 'de browser op het menu (<b>⋮</b>) rechtsboven'}`],
        [ICON_ADD, 'Kies <b>App installeren</b> of <b>Toevoegen aan startscherm</b>'],
        ['<img src="/assets/icon-192.png" width="22" height="22" alt="" style="border-radius:5px">', 'Open <b>Kanaal</b> vanaf je startscherm'],
      ]));
  }
  return el('p.small', {}, 'In Chrome of Edge op een computer: het installatie-icoon rechts in de adresbalk. Maar dit spel is gemaakt voor je smartphone.');
}

export type InstallChoice = 'installed' | 'browser';

/**
 * Asks the player to install the game. Resolves with 'browser' when they continue without,
 * or 'installed' when the system dialog was accepted (Android).
 */
export async function askToInstall(opts: { title: string; quote: string; label?: string; continueLabel?: string }): Promise<InstallChoice> {
  const info = detect();
  const sheet = openSheet({ label: opts.label ?? 'Buiten het verhaal', title: opts.title, quote: opts.quote });
  const benefits = el('ul.benefits', {},
    el('li', {}, 'Volledig scherm, zonder adresbalk'),
    el('li', {}, 'Joris kan je een melding sturen, ook als het spel dicht is'),
    el('li', {}, 'Niets te downloaden uit een app store'));
  const body = el('div', {});
  const actions = el('div.sheet__actions', {});
  const later = el('button.btn.btn--ghost', { type: 'button' }, opts.continueLabel ?? 'Speel in de browser');
  sheet.body.append(benefits, body, actions);

  const already = await installedElsewhere();
  const render = () => {
    const now = detect();
    body.replaceChildren();
    actions.replaceChildren();
    if (already) {
      body.append(el('p.sheet__feedback.is-good', {}, 'Kanaal staat al op je startscherm. Open het van daaruit voor de beste ervaring.'));
      actions.append(later);
      return;
    }
    if (now.canPrompt) {
      const install = el('button.btn.btn--primary', { type: 'button' }, 'Installeer Kanaal');
      install.addEventListener('click', async () => {
        if (!deferred) return;
        await deferred.prompt();
        const { outcome } = await deferred.userChoice;
        backstage.log('sensor', `Installatievenster: ${outcome === 'accepted' ? 'aanvaard' : 'geweigerd'}`, 'Chromium: <code>beforeinstallprompt</code> → systeemvenster.');
        deferred = null;
        if (outcome === 'accepted') {
          body.replaceChildren(el('p.sheet__feedback.is-good', {}, 'Geïnstalleerd. Open Kanaal vanaf je startscherm, of speel hier verder.'));
          actions.replaceChildren(later);
          later.textContent = 'Hier verder spelen';
          installed = true;
        } else render();
      });
      actions.append(install, later);
    } else {
      body.append(instructions(now));
      actions.append(later);
    }
  };
  let installed = false;
  listeners.add(render);
  render();
  backstage.log('sensor', 'Niet geïnstalleerd', `Platform <code>${info.platform}</code>, browser <code>${info.browser}</code>${info.inApp ? `, in-app browser <code>${info.inApp}</code>` : ''}. ${info.canPrompt ? 'Systeemvenster beschikbaar (<code>beforeinstallprompt</code>).' : info.platform === 'ios' ? 'iOS heeft geen installatievenster: we tonen de stappen.' : 'Geen systeemvenster: we tonen de stappen.'}`);

  const result = await new Promise<InstallChoice>((r) => later.addEventListener('click', () => r(installed ? 'installed' : 'browser'), { once: true }));
  listeners.delete(render);
  await sheet.close();
  return result;
}
